import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { User, Role, Workspace } from '@/lib/types';
import api from '@/utils/api';

export interface AuthContextType {
  user: User | null;
  role: Role | null;
  workspace: Workspace | null;
  defaultWorkspaceId: number | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User>;
  establishSession: (data: any) => User;
  logout: () => void;
  changeWorkspace: (workspace: Workspace) => void;
  changeRole: (role: Role) => void;
  changeDefaultWorkspaceId: (id: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const setLocalStorageItem = (key: string, value: any) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const getLocalStorageItem = (key: string) => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

type AuthState = {
  user: User | null;
  role: Role | null;
  workspace: Workspace | null;
  defaultWorkspaceId: number | null;
  isAuthenticated: boolean;
};

const signedOut: AuthState = {
  user: null,
  role: null,
  workspace: null,
  defaultWorkspaceId: null,
  isAuthenticated: false,
};

// The access token lives in localStorage, which every tab shares, so a tab is
// signed in whenever that token is present and unexpired — the same check the
// API makes (it rejects tokens without an exp). Gating on a per-tab flag made
// each new tab, including links opened from an assistant, ask for a login.
const hasLiveToken = () => {
  const token = localStorage.getItem('accessToken');
  if (!token) return false;
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return !!exp && exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

const storedState = (): AuthState => ({
  user: getLocalStorageItem('user'),
  role: getLocalStorageItem('role'),
  workspace: getLocalStorageItem('workspace'),
  defaultWorkspaceId: getLocalStorageItem('defaultWorkspaceId'),
  isAuthenticated: hasLiveToken(),
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(storedState);

  // Follow sign-ins, sign-outs and account switches made in other tabs. Each
  // writes accessToken last (and a workspace switch rewrites it too), so the
  // user record is already in place when the event arrives.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== localStorage || (e.key !== null && e.key !== 'accessToken')) return;
      setAuthState((prev) => {
        if (!hasLiveToken()) return prev.isAuthenticated ? signedOut : prev;
        const next = storedState();
        // Same user (e.g. a workspace switch): keep this tab's own workspace.
        return prev.isAuthenticated && next.user?.id === prev.user?.id ? prev : next;
      });
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // establishSession applies a login payload (from password login OR SSO code
  // exchange — both return the same shape) to context + storage. Centralising
  // it keeps the two auth paths byte-for-byte identical downstream.
  const establishSession = (data: any) => {
    const workspace = {
      ...data.default_workspace,
      has_payment_methods: data.has_payment_methods,
    };

    setAuthState({
      user: data,
      role: data.role,
      workspace,
      defaultWorkspaceId: data.default_workspace_id,
      isAuthenticated: true,
    });

    setLocalStorageItem('user', data);
    setLocalStorageItem('role', data.role);
    setLocalStorageItem('workspace', workspace);
    setLocalStorageItem('defaultWorkspaceId', data.default_workspace_id);
    localStorage.setItem('accessToken', data.access_token);

    return data;
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post(`auth/login`, {
        email,
        password,
      });

      return establishSession(response.data);
    } catch (err: any) {
      if (err.response && err.response.data && err.response.data.message) {
        throw new Error(`Login failed: ${err.response.data.message}`);
      } else {
        throw err ? err : new Error('Login failed');
      }
    }
  };

  const logout = () => {
    setAuthState({
      user: null,
      role: null,
      workspace: null,
      defaultWorkspaceId: null,
      isAuthenticated: false,
    });

    localStorage.removeItem('user');
    localStorage.removeItem('role');
    localStorage.removeItem('workspace');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('defaultWorkspaceId');
  };

  const changeWorkspace = (workspace: Workspace) => {
    setAuthState((prev) => ({ ...prev, workspace }));
    setLocalStorageItem('workspace', workspace);
  };

  const changeRole = (role: Role) => {
    setAuthState((prev) => ({ ...prev, role }));
    setLocalStorageItem('role', role);
  };

  const changeDefaultWorkspaceId = (id: number) => {
    setAuthState((prev) => ({ ...prev, defaultWorkspaceId: id }));
    setLocalStorageItem('defaultWorkspaceId', id);
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        establishSession,
        logout,
        changeWorkspace,
        changeRole,
        changeDefaultWorkspaceId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
